
import { Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, firstValueFrom, map, Observable, tap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import OSRMTextInstructions from 'osrm-text-instructions';

@Component({
    imports: [
        CommonModule,
        FormsModule
    ],
    standalone: true,
    templateUrl: './new-testing-route.component.html',
    styleUrls: ['./new-testing-route.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class NewTestingRouteComponent {
    private http = inject(HttpClient);
    public jsonData: any = null;
    public finalData = signal(null);

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) {
            console.warn('No se seleccionó ningún archivo.');
            return;
        }

        const file: File = input.files[0];
        if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
            console.error('El archivo seleccionado no es de tipo JSON.');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            try {
                const texto = reader.result as string;
                this.jsonData = JSON.parse(texto);

                console.log('JSON cargado:', this.jsonData);
                this.mapData();
            } catch (err) {
                console.error('Error al parsear JSON:', err);
            }
        };

            reader.onerror = (error) => {
            console.error('Error al leer el archivo:', error);
        };

        reader.readAsText(file);
    }

    async mapData() {
        const allDataPromises = this.jsonData.map(async (data, index) => {
            const filteredItems = data.points?.filter(point => point.type !== 'add') || [];
            let startLocation = {
                lat: data?.startPoint?.latitude,
                lng: data?.startPoint?.longitude
            };

            const infoItemsPromise = filteredItems.map(async (point, pointIndex) => {
                if (pointIndex > 0) {
                    const prev = filteredItems[pointIndex - 1];
                    startLocation = {
                        lat: Number(prev.point.latitude),
                        lng: Number(prev.point.longitude)
                    };
                }

                const item: any = {
                    id: pointIndex,
                    img: point.point.image,
                    status: 'pending',
                    title: point.point.name?.['es'],
                    startDate: null,
                    durationMinutes: point.point.duration,
                    location: {
                        lat: Number(point.point.latitude),
                        lng: Number(point.point.longitude)
                    },
                    segmentTime: {
                        start: point.start,
                        end: point.end
                    },
                    segmentTrame: {
                        startPoint: { ...startLocation },
                        endPoint: {
                            lat: Number(point.point.latitude),
                            lng: Number(point.point.longitude)
                        }
                    },
                    polylinesPath: [],
                    wayPoints: [],
                    transfers: []
                };

                const coords = `${item.segmentTrame.startPoint.lng},${item.segmentTrame.startPoint.lat};` +
                                `${item.segmentTrame.endPoint.lng},${item.segmentTrame.endPoint.lat}`;

                const { polylines, transfers } = await this.getRoutedata(coords);
                item.polylinesPath = polylines;
                item.transfers = transfers;

                return item;
            });

            const puntosConInfo = await Promise.all(infoItemsPromise);
            return {
                id: index + 1,
                date: data.date,
                weatherIconName: 'partly-cloudy-day',
                points: puntosConInfo
            };
        });

        const datosProcesados = await Promise.all(allDataPromises);
        this.finalData.set(datosProcesados);
    }

    downloadJSON(){
        const data = {
            id: 1,
            chatId: '24',
            dailyRoutes: this.finalData()
        }
        const jsonString = JSON.stringify(data, null, 2); 
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'route.json'; // nombre que tendrá el archivo al descargarse
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

    }

    async getRoutedata(coords: string): Promise<{ polylines: any; transfers: any }> {
        const polylines$ = this.getPolylines(coords);
        const transfers$ = this.getTransfers(coords);
        const promisePolylines = firstValueFrom(polylines$);
        const promiseTransfers = firstValueFrom(transfers$);

        const [polylines, transfers] = await Promise.all([promisePolylines, promiseTransfers]);
        return { polylines, transfers };
    }

    getPolylines(coords): Observable<any> {
        const url = `https://cms-iurban.com/osrm_foot/route/v1/driving/${coords}?steps=true&overview=full`;
        return this.http.get<any>(url).pipe(
            map(res => res.routes[0].geometry),
            map(geo => this.polylineDecode(geo, 5)),
            catchError(err => {
                console.error('Error fetching route geometry', err);
                return throwError(() => new Error('HTTP request failed', { cause: err }));
            })
        );
    }

    getTransfers(coords): Observable<any> {
        const url = `https://cms-iurban.com/osrm_foot/route/v1/driving/${coords}?steps=true&overview=full`;
        return this.http.get<any>(url).pipe(
            map(res => res.routes[0]),
            map(transfers => this.getRouteInfo(transfers)),
            catchError(err => {
                console.error('Error fetching route geometry', err);
                return throwError(() => new Error('HTTP request failed', { cause: err }));
            })
        );
    }

    getRouteInfo(transfers){
        const version = 'v5';
        const osrmTextInstructions = OSRMTextInstructions(version);        
        return transfers.legs?.map((item, index) => {
            item.steps = item.steps.map(step => {
                const instruction = osrmTextInstructions.compile('es', step, {});
                return {
                    instruction,
                    distance: step.distance,
                    duration: step.duration,
                    location: {lat: step.maneuver.location?.[1], lng: step.maneuver.location?.[0]},
                    mode: step.mode
                }
            });
            return {
                distance: item.distance,
                duration: item.duration,
                transfers: item.steps
            };
        })?.[0];
    }

    polylineDecode(str: string, prec = 6): google.maps.LatLngLiteral[] {
        let index = 0, lat = 0, lng = 0, shift = 0, result = 0, byte = 0;
        const coords: google.maps.LatLngLiteral[] = [];
        const factor = Math.pow(10, prec);
        while (index < str.length) {
            shift = result = 0;
            do {
                byte = str.charCodeAt(index++) - 63;
                result |= (byte & 0x1f) << shift;
                shift += 5;
            } while (byte >= 0x20);
            const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);

            shift = result = 0;
            do {
                byte = str.charCodeAt(index++) - 63;
                result |= (byte & 0x1f) << shift;
                shift += 5;
            } while (byte >= 0x20);
            const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);

            lat += dlat; lng += dlng;
            coords.push({ lat: lat / factor, lng: lng / factor });
        }
        return coords;
    }
}